import { useState } from "react";
import { IconStar, IconTrash } from "@tabler/icons-react";
import { toast } from "sonner";
import { ContactPerson, CustomerOrganization } from "../../utils/interfaces";
import {
  useUpdateContactMutation,
  useDeleteContactMutation,
  useSetPrimaryContactMutation,
} from "../../api/queries/customerOrganizationQueries";
import { ContactPositionLevelSelector } from "./ContactPositionLevelSelector";
import { useAuth } from "../../hooks/useAuth";

interface CustomerContactRowProps {
  contact: ContactPerson;
  org: CustomerOrganization;
  onPrimaryEmailEdit: (contact: ContactPerson) => void;
  onUpdated?: () => void;
}

export const CustomerContactRow = ({
  contact,
  org,
  onPrimaryEmailEdit,
  onUpdated,
}: CustomerContactRowProps) => {
  const { canMutateOrg } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [escalationLevelOpen, setEscalationLevelOpen] = useState(false);
  const [name, setName] = useState(contact.name);
  const [email, setEmail] = useState(contact.email);
  const [phone, setPhone] = useState(contact.phone_number ?? "");

  const updateMutation = useUpdateContactMutation();
  const deleteMutation = useDeleteContactMutation();
  const setPrimaryMutation = useSetPrimaryContactMutation();

  const handleSave = async () => {
    try {
      if (contact.is_primary && email.trim() !== contact.email) {
        onPrimaryEmailEdit({ ...contact, email: email.trim() });
        return;
      }
      await updateMutation.mutateAsync({
        orgId: org.id,
        contactId: contact.id,
        name: name.trim(),
        email: email.trim(),
        phone_number: phone.trim() || null,
      });
      setIsEditing(false);
      onUpdated?.();
      toast.success("Contact updated");
    } catch {
      toast.error("Failed to update contact");
    }
  };

  const handleToggleAutomated = async () => {
    try {
      await updateMutation.mutateAsync({
        orgId: org.id,
        contactId: contact.id,
        receives_automated_emails: !contact.receives_automated_emails,
      });
      onUpdated?.();
    } catch {
      toast.error("Failed to update contact");
    }
  };

  const handlePositionChange = async (positionCode: number | null) => {
    if ((contact.position_code ?? null) === positionCode) return;
    try {
      await updateMutation.mutateAsync({
        orgId: org.id,
        contactId: contact.id,
        position_code: positionCode,
      });
      onUpdated?.();
      toast.success(
        positionCode == null
          ? "Position level cleared"
          : `Position level set to ${positionCode}`,
      );
    } catch {
      toast.error("Failed to update position level");
    }
  };

  const handleDelete = async () => {
    if (contact.is_primary) return;
    try {
      await deleteMutation.mutateAsync({
        orgId: org.id,
        contactId: contact.id,
      });
      onUpdated?.();
      toast.success("Contact removed");
    } catch {
      toast.error("Failed to remove contact");
    }
  };

  const handleSetPrimary = async () => {
    if (contact.is_primary) return;
    try {
      await setPrimaryMutation.mutateAsync({
        orgId: org.id,
        contactId: contact.id,
      });
      onUpdated?.();
      toast.success("Primary contact updated");
    } catch {
      toast.error("Failed to set primary contact");
    }
  };

  const escalationLevelSummary =
    contact.position_code && contact.position_code > 1
      ? `Pos ${contact.position_code}`
      : "None";

  return (
    <div
      className={`rounded-xl border px-4 py-3 ${
        contact.receives_automated_emails
          ? "border-slate-200 bg-white"
          : "border-amber-200 bg-amber-50/40"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {contact.is_primary ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF4F2] px-2 py-0.5 text-xs font-medium text-[#4B7F73]">
                <IconStar size={12} /> Primary
              </span>
            ) : canMutateOrg ? (
              <button
                type="button"
                onClick={handleSetPrimary}
                className="text-xs text-slate-500 hover:text-[#4B7F73]"
              >
                Make primary
              </button>
            ) : null}
            {contact.position_code && contact.position_code > 1 ? (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                Pos {contact.position_code}
              </span>
            ) : null}
          </div>

          {isEditing ? (
            <div className="space-y-2 pt-1">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                type="email"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional)"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="rounded-md bg-[#4B7F73] px-3 py-1.5 text-sm text-white"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="font-medium text-slate-900">{contact.name}</p>
              <a
                href={`mailto:${contact.email}`}
                className="text-sm text-blue-600 hover:underline"
              >
                {contact.email || "No email"}
              </a>
              {contact.phone_number ? (
                <p className="text-sm text-slate-500">{contact.phone_number}</p>
              ) : null}
              {!contact.is_primary && canMutateOrg ? (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left"
                    aria-expanded={escalationLevelOpen}
                    onClick={() => setEscalationLevelOpen((open) => !open)}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-600">
                        Escalation level
                      </p>
                      {!escalationLevelOpen ? (
                        <p className="text-xs text-slate-500">
                          {escalationLevelSummary}
                        </p>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-xs text-slate-400">
                      {escalationLevelOpen ? "▲" : "▼"}
                    </span>
                  </button>
                  {escalationLevelOpen ? (
                    <div className="mt-2">
                      <ContactPositionLevelSelector
                        value={contact.position_code}
                        disabled={updateMutation.isPending}
                        hideHeader
                        onChange={(code) => void handlePositionChange(code)}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>

        {!isEditing && canMutateOrg && (
          <div className="flex shrink-0 items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <span>Automated emails</span>
              <button
                type="button"
                role="switch"
                aria-checked={contact.receives_automated_emails}
                onClick={handleToggleAutomated}
                className={`relative inline-flex h-6 w-10 items-center rounded-full transition ${
                  contact.receives_automated_emails
                    ? "bg-[#4B7F73]"
                    : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    contact.receives_automated_emails
                      ? "translate-x-4"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </label>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Edit
            </button>
            {!contact.is_primary && (
              <button
                type="button"
                onClick={handleDelete}
                aria-label="Delete contact"
                className="text-slate-400 hover:text-red-600"
              >
                <IconTrash size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
